import { useState } from 'react';
import Navbar from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MapPin, Mail, Globe, Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setLoading(false);
    setSubmitted(true);
    toast.success('Message sent successfully!');
  };

  return (
    <div className="min-h-screen bg-ags-bg">
      <Navbar />
      
      {/* Hero */}
      <section className="py-16 md:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl text-ags-text tracking-tight leading-tight mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-ags-text-secondary leading-relaxed">
              Have questions about AGS-HK? Want to get involved? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-ags-blue/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-ags-blue" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-medium text-ags-text mb-1">Location</h3>
                      <p className="text-sm text-ags-text-secondary">
                        Hong Kong SAR, China
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-ags-blue/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-ags-blue" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-medium text-ags-text mb-1">Email</h3>
                      <a 
                        href="mailto:info@ghanascholarshk.org" 
                        className="text-sm text-ags-blue hover:underline"
                      >
                        info@ghanascholarshk.org
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-ags-blue/10 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-5 h-5 text-ags-blue" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-medium text-ags-text mb-1">Website</h3>
                      <a 
                        href="https://ghanascholarshk.org" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-ags-blue hover:underline"
                      >
                        ghanascholarshk.org
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="border-slate-200 lg:col-span-2">
              <CardContent className="p-6 md:p-8">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-heading text-xl text-ags-text mb-2">Message Sent!</h3>
                    <p className="text-ags-text-secondary mb-6">
                      Thank you for reaching out. We'll get back to you as soon as possible.
                    </p>
                    <Button onClick={() => setSubmitted(false)} variant="outline">
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <>
                    <h2 className="font-heading text-xl text-ags-text mb-6">Send us a Message</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Name *</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            required
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            required
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          value={formData.subject}
                          onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                          required
                          data-testid="contact-subject-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          value={formData.message}
                          onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                          rows={5}
                          required
                          data-testid="contact-message-input"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full md:w-auto bg-ags-blue hover:bg-ags-blue/90"
                        disabled={loading}
                        data-testid="contact-submit-btn"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                        Send Message
                      </Button>
                    </form>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center text-sm text-ags-text-muted">
          <p>© {new Date().getFullYear()} Association of Ghanaian Scholars in Hong Kong. All rights reserved.</p>
          <p className="mt-2">Built by Asamoah Joseph</p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
